import * as React from 'react';

import {useMutation} from '@apollo/react-hooks';

import type {
    CreateEdition as CreateEditionMutation,
    CreateEditionVariables as CreateEditionMutationVariables,
    DuplicateEdition as DuplicateEditionMutation,
    DuplicateEditionVariables as DuplicateEditionMutationVariables,
    DeleteEdition as DeleteEditionMutation,
    DeleteEditionVariables as DeleteEditionMutationVariables,
    RestoreEdition as RestoreEditionMutation,
    RestoreEditionVariables as RestoreEditionMutationVariables,
    UpdateEdition as UpdateEditionMutation,
    UpdateEditionVariables as UpdateEditionMutationVariables,
    GetNewsletterEditions as GetEditionsQuery,
    EditionUpdateInput,
    GetEdition as GetEditionQuery,
    FieldsToCompare,
} from 'nutshell-graphql-types';

import CREATE_EDITION from '../graphql/mutations/create-edition.graphql';
import DELETE_EDITION from '../graphql/mutations/delete-edition.graphql';
import DUPLICATE_EDITION from '../graphql/mutations/duplicate-edition.graphql';
import RESTORE_EDITION from '../graphql/mutations/restore-edition.graphql';
import UPDATE_EDITION from '../graphql/mutations/update-edition.graphql';
import GET_EDITION from '../graphql/queries/get-edition.graphql';
import {getNewsletterEditionListQueryVariables} from '../helpers';

import GET_EDITIONS from './graphql/queries/get-editions.graphql';

type Props = {
    children: (arg1: {
        onCreate: (
            name: string,
            campaignId: string,
            isABTested?: boolean,
            fieldsToCompare?: FieldsToCompare
        ) => Promise<any>;
        onDuplicate: (
            editionId: string,
            campaignId: string,
            name?: string | null | undefined
        ) => Promise<any>;
        onDelete: (editionId: string, campaignId: string) => Promise<any>;
        onRestore: (editionId: string, campaignId: string) => Promise<any>;
        onUpdate: (input: EditionUpdateInput) => Promise<any>;
    }) => React.ReactElement;
};

export function EditionMutations(props: Props) {
    const [createEdition] = useMutation<CreateEditionMutation, CreateEditionMutationVariables>(
        CREATE_EDITION
    );

    const [duplicateEdition] = useMutation<
        DuplicateEditionMutation,
        DuplicateEditionMutationVariables
    >(DUPLICATE_EDITION);

    const [deleteEdition] = useMutation<DeleteEditionMutation, DeleteEditionMutationVariables>(
        DELETE_EDITION
    );

    const [restoreEdition] = useMutation<RestoreEditionMutation, RestoreEditionMutationVariables>(
        RESTORE_EDITION
    );

    const [updateEdition] = useMutation<UpdateEditionMutation, UpdateEditionMutationVariables>(
        UPDATE_EDITION
    );

    return props.children({
        onCreate: (
            name: string,
            campaignId: string,
            isABTested?: boolean,
            fieldsToCompare?: FieldsToCompare
        ) => {
            return createEdition({
                variables: {
                    edition: {
                        name: name,
                        campaignId: campaignId,
                        isABTested: isABTested,
                        fieldsToCompare: fieldsToCompare,
                    },
                },
                update: (proxy, mutationResults) => {
                    const newEdition =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.editionCreate &&
                        mutationResults.data.editionCreate.edition;

                    if (!newEdition) return;

                    editionCacheAdd(proxy, newEdition, campaignId, false);
                },
            }).then(
                (mutationResults) =>
                    mutationResults &&
                    mutationResults.data &&
                    mutationResults.data.editionCreate &&
                    mutationResults.data.editionCreate.edition
            );
        },
        onDuplicate: (editionId: string, campaignId: string, name?: string | null) => {
            return duplicateEdition({
                variables: {edition: {editionId: editionId, name: name, campaignId: campaignId}},
                update: (proxy, mutationResults) => {
                    const newEdition =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.editionDuplicateToNewsletterEdition &&
                        mutationResults.data.editionDuplicateToNewsletterEdition.edition;

                    if (!newEdition) return;

                    editionCacheAdd(proxy, newEdition, campaignId, false);
                },
            }).then(
                (mutationResults) =>
                    mutationResults &&
                    mutationResults.data &&
                    mutationResults.data.editionDuplicateToNewsletterEdition &&
                    mutationResults.data.editionDuplicateToNewsletterEdition.edition
            );
        },
        onDelete: (editionId: string, campaignId: string) => {
            return deleteEdition({
                variables: {id: editionId},
                update: (proxy, mutationResults) => {
                    const deletedEdition =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.editionDelete &&
                        mutationResults.data.editionDelete.edition;

                    if (!deletedEdition) return;

                    // Remove from the unarchived cache, add to the archived
                    // cache
                    editionCacheRemove(proxy, deletedEdition, campaignId, false);
                    editionCacheAdd(proxy, deletedEdition, campaignId, true);
                },
            });
        },
        onRestore: (editionId: string, campaignId: string) => {
            return restoreEdition({
                variables: {id: editionId},
                update: (proxy, mutationResults) => {
                    const restoredEdition =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.editionRestore &&
                        mutationResults.data.editionRestore.edition;

                    if (!restoredEdition) return;

                    // Remove from the archived cache, add to the unarchived
                    // cache
                    editionCacheRemove(proxy, restoredEdition, campaignId, true);
                    editionCacheAdd(proxy, restoredEdition, campaignId, false);
                },
            });
        },
        onUpdate: (saveData: EditionUpdateInput) => {
            return updateEdition({
                variables: {
                    edition: saveData,
                },
                update: (proxy, mutationResults) => {
                    // Read the data from our cache for this query.
                    const cacheResult: GetEditionQuery | null | undefined = proxy.readQuery({
                        query: GET_EDITION,
                        variables: {editionId: saveData.editionId},
                    });

                    if (!cacheResult || !cacheResult.edition) {
                        return;
                    }

                    const updatedEdition =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.editionUpdate &&
                        mutationResults.data.editionUpdate.edition;
                    if (!updatedEdition) return;

                    proxy.writeQuery({
                        query: GET_EDITION,
                        variables: {editionId: saveData.editionId},
                        data: {
                            ...cacheResult.edition,
                            edition: updatedEdition,
                        },
                    });
                },
            });
        },
    });
}

/**
 * This will handle updating our local cache when we archive or restore a
 * edition. It correctly handles updating both the archived and unarchived
 * caches.
 *
 * @param {DataProxy} proxy The mutation update proxy
 * @param {Edition} edition The edition that was mutated
 * @param {boolean} campaignId The campaign id to update the cache of
 * @param {boolean} isArchived Are we updating the archived list or not
 */
// @ts-expect-error
const editionCacheAdd = (proxy, edition, campaignId: string, isArchived: boolean) => {
    // Passing a value for `isArchived` here isn't really needed because we
    // overwrite it when writing to the cache below, but hey I made it required
    const variables = getNewsletterEditionListQueryVariables(campaignId, isArchived);

    // Update our unarchived cache
    // Again, not the best name. We're adding an edition to this cache
    try {
        const cacheResult: GetEditionsQuery | null | undefined = proxy.readQuery({
            query: GET_EDITIONS,
            variables: variables,
        });

        if (!cacheResult || !cacheResult.newsletter) {
            return;
        }

        proxy.writeQuery({
            query: GET_EDITIONS,
            variables: variables,
            data: {
                newsletter: {
                    ...cacheResult.newsletter,
                    editions: {
                        ...cacheResult.newsletter.editions,
                        edges: [
                            {
                                cursor: edition.id,
                                node: edition,
                                __typename: 'EditionsEdge',
                            },
                        ].concat(cacheResult.newsletter.editions.edges),
                    },
                },
            },
        });
    } catch {
        // If the above query doesn't exist in the cache, Apollo will throw an
        // error (why it can't just return null is beyond me). If that happens,
        // we need to "catch" the error. But we don't want to really do anything
        // with it. The just means we haven't performed this query yet. So, an
        // empty catch block
    }
};

/**
 * This will handle updating our local cache when we archive an
 * edition.
 *
 * @param {DataProxy} proxy The mutation update proxy
 * @param {Edition} edition The edition that was mutated
 * @param {boolean} campaignId The campaign id to update the cache of
 * @param {boolean} isArchived Are we updating the archived list or not
 */
// @ts-expect-error
const editionCacheRemove = (proxy, edition, campaignId: string, isArchived: boolean) => {
    // Passing a value for `isArchived` here isn't really needed because we
    // overwrite it when writing to the cache below, but hey I made it required
    const variables = getNewsletterEditionListQueryVariables(campaignId, isArchived);

    // Update our unarchived cache
    // Again, not the best name. We're adding an edition to this cache
    try {
        const cacheResult: GetEditionsQuery | null | undefined = proxy.readQuery({
            query: GET_EDITIONS,
            variables: variables,
        });

        if (!cacheResult || !cacheResult.newsletter) {
            return;
        }

        proxy.writeQuery({
            query: GET_EDITIONS,
            variables: variables,
            data: {
                newsletter: {
                    ...cacheResult.newsletter,
                    editions: {
                        ...cacheResult.newsletter.editions,
                        edges: cacheResult.newsletter.editions.edges.filter(
                            (edge) => edge.node.id !== edition.id
                        ),
                    },
                },
            },
        });
    } catch {
        // If the above query doesn't exist in the cache, Apollo will throw an
        // error (why it can't just return null is beyond me). If that happens,
        // we need to "catch" the error. But we don't want to really do anything
        // with it. The just means we haven't performed this query yet. So, an
        // empty catch block
    }
};
